/**
 * HouseholdMembersGrid — full-width member roster on the Household Profile.
 *
 * Each member card shows:
 *   • Applicant Name
 *   • NTN Recommendation
 *   • Workflow Status (current stage)
 *   • View Applicant button → /screenings/{id}
 *
 * Replaces the legacy MemberCard component. Visually consistent with the
 * Applicant Profile's executive language — clean, scannable, no clutter.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { stageFor } from "@/lib/verifyHub";
import MemberDocumentsDialog from "@/components/MemberDocumentsDialog";
import { fmtDate } from "@/lib/dateFormat";
import { ArrowRight, Crown, User as UserIcon, CheckCircle2, AlertTriangle, Gavel, UserMinus } from "lucide-react";

const REC_VISUAL = {
  approve:     { label: "Recommended",                cls: "bg-emerald-700 text-white",     Icon: CheckCircle2 },
  conditional: { label: "Recommended with Conditions",cls: "bg-amber-600 text-white",       Icon: AlertTriangle },
  deny:        { label: "Not Recommended",            cls: "bg-rose-700 text-white",        Icon: Gavel },
};

const STAGE_TONE = {
  neutral: "bg-slate-100 text-slate-700 ring-slate-200",
  amber:   "bg-amber-50 text-amber-800 ring-amber-200",
  violet:  "bg-violet-50 text-violet-700 ring-violet-200",
  blue:    "bg-blue-50 text-blue-700 ring-blue-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

/* v1.0.8 — staff-only soft removal with explicit confirmation + reason. */
function RemoveMemberDialog({ group, m, onRemoved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`/groups/${group.id}/members/${m.screening_id}`,
        { params: reason.trim() ? { reason: reason.trim() } : {} });
      toast.success("Member removed from household — screening history preserved");
      onRemoved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Removal failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="w-full rounded-md h-9 border-rose-200 text-rose-700 hover:border-rose-500 hover:bg-rose-50"
                data-testid={`remove-member-${m.screening_id}`}>
          <UserMinus className="h-3.5 w-3.5 mr-1.5" /> Remove from household
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`remove-member-dialog-${m.screening_id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {m.applicant_name} from this household?</AlertDialogTitle>
          <AlertDialogDescription>
            This marks them as no longer part of the active rental household. Their screening,
            reports, documents, verifications and notes are fully preserved, and they will remain
            visible here as a historical &ldquo;Removed&rdquo; card. Household FICO, rollups,
            workflow and decisions will recompute without them. No email or notification is sent.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (recorded in the audit log) — e.g. applicant no longer moving in"
                  data-testid={`remove-member-reason-${m.screening_id}`} />
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={remove} disabled={busy}
                             className="bg-rose-700 hover:bg-rose-800"
                             data-testid={`remove-member-confirm-${m.screening_id}`}>
            {busy ? "Removing…" : "Remove member"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MemberCard({ m, canUpload, canRemove, group, onRemoved }) {
  // Adapter — member_screenings expose: role, applicant_name, overall_status,
  // dp.{final_verdict | suggested_verdict}. The legacy MemberCard already
  // followed this contract; new card mirrors it.
  const fullName = (m.applicant_name && m.applicant_name.trim()) || "Applicant";
  const nameParts = fullName.split(/\s+/);
  const initials = nameParts.length >= 2
    ? (nameParts[0][0] || "") + (nameParts[nameParts.length - 1][0] || "")
    : (nameParts[0]?.slice(0, 2) || "");
  const isPrimary = m.role === "primary";
  // v1.0.5 — subscriber-facing member cards display ONLY the staff-posted
  // individual recommendation. System-calculated DP verdicts are NTN-internal
  // decision support and must never be presented here as a recommendation.
  const verdictRaw = m.recommendation || null;
  const rec = verdictRaw ? REC_VISUAL[verdictRaw] : null;
  // stageFor needs a screening-shaped object. member_screenings carries
  // overall_status but not verify_status, so synthesise a minimal shape.
  const stage = stageFor({ verify_status: m.verify_status, overall_status: m.overall_status });
  const RecIcon = rec?.Icon || Gavel;
  const removed = !!m.removed;

  return (
    <div
      className={`bg-white rounded-lg ring-1 shadow-sm transition-all border-l-4 p-5 ${removed
        ? "ring-slate-200 border-l-slate-300 opacity-60 grayscale"
        : "ring-slate-200 hover:shadow-md border-l-slate-200 hover:border-l-slate-900"}`}
      data-testid={`household-member-card-${m.screening_id}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-12 w-12 rounded-full bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center shrink-0">
          <span className="font-serif text-base text-slate-700">{initials.toUpperCase() || <UserIcon className="h-5 w-5" />}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="ntn-h3 truncate" data-testid={`member-name-${m.screening_id}`}>{fullName}</h3>
            {isPrimary && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ntn-eyebrow bg-amber-50 text-amber-800 ring-1 ring-amber-200">
                <Crown className="h-2.5 w-2.5" /> Primary
              </span>
            )}
            {removed && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ntn-eyebrow bg-slate-100 text-slate-600 ring-1 ring-slate-300"
                    data-testid={`member-removed-badge-${m.screening_id}`}>
                <UserMinus className="h-2.5 w-2.5" /> Removed from Household
              </span>
            )}
          </div>
          <div className="ntn-mono mt-0.5">ID · {m.screening_id.slice(0, 8).toUpperCase()}</div>
        </div>
      </div>

      {/* NTN Recommendation */}
      <div className="space-y-2 mb-4">
        <div className="ntn-eyebrow">NTN Recommendation</div>
        {rec ? (
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold text-xs ${rec.cls} shadow-sm`} data-testid={`member-rec-${m.screening_id}`}>
            <RecIcon className="h-3.5 w-3.5" /> {rec.label}
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-slate-600 bg-slate-50 ring-1 ring-slate-200">
            Pending
          </div>
        )}
      </div>

      {/* Workflow status */}
      <div className="space-y-2 mb-4">
        <div className="ntn-eyebrow">Workflow Status</div>
        {removed ? (
          <div className="ntn-caption text-slate-500" data-testid={`member-stage-${m.screening_id}`}>
            No longer part of the active household
            {m.removed_at && <span className="ntn-mono block mt-0.5">Removed {fmtDate(m.removed_at)}</span>}
          </div>
        ) : (
          <Badge variant="outline" className={`ntn-eyebrow border-0 ring-1 ring-inset px-2.5 py-1 rounded-full ${STAGE_TONE[stage.tone] || STAGE_TONE.neutral}`}
                 data-testid={`member-stage-${m.screening_id}`}>
            {stage.label}
          </Badge>
        )}
      </div>

      {/* CTA */}
      <div className="space-y-2">
        <Link to={`/screenings/${m.screening_id}`}>
          <Button className="w-full bg-slate-900 hover:bg-slate-800 rounded-md h-10" data-testid={`member-view-${m.screening_id}`}>
            View Applicant <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </Link>
        {canUpload && !removed && (
          <MemberDocumentsDialog
            screeningId={m.screening_id}
            memberName={fullName}
          />
        )}
        {canRemove && !removed && !isPrimary && (
          <RemoveMemberDialog group={group} m={m} onRemoved={onRemoved} />
        )}
      </div>
    </div>
  );
}

export default function HouseholdMembersGrid({ group, testid = "household-members-grid", onChanged }) {
  const { user } = useAuth();
  const canUpload = user?.role !== "applicant" && !group.archived_at;
  // v1.0.8 — member removal is NTN-staff only (backend-enforced too).
  const canRemove = user?.role === "ntn_staff" && !group.archived_at;
  const members = (group.member_screenings || []).slice().sort((a, b) => {
    // Removed members sink to the end; primary leads the active set.
    if (!!a.removed !== !!b.removed) return a.removed ? 1 : -1;
    if (a.role === "primary" && b.role !== "primary") return -1;
    if (b.role === "primary" && a.role !== "primary") return 1;
    return 0;
  });
  const activeCount = members.filter((m) => !m.removed).length;
  const removedCount = members.length - activeCount;

  if (members.length === 0) return null;

  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="ntn-eyebrow">Household Members</span>
        <span className="text-slate-300">·</span>
        <span className="ntn-caption font-medium">
          {activeCount} active applicant{activeCount === 1 ? "" : "s"}
          {removedCount > 0 && ` · ${removedCount} removed`}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {members.map((m) => (
          <MemberCard key={m.screening_id} m={m} canUpload={canUpload}
                      canRemove={canRemove} group={group} onRemoved={onChanged} />
        ))}
      </div>
    </section>
  );
}
